import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ChevronDown, Settings, MessageCircle, LogOut, BarChart3 } from 'lucide-react';
import { hasMinimumAdminAccess } from '../../types/roles';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

interface ProfileDropdownProps {
  onProfileClick: () => void;
  onFeedbackClick: () => void;
  /** Show user display name next to avatar */
  showName?: boolean;
  /** Additional menu items rendered before Sign Out. Receives onClose to dismiss dropdown. */
  additionalItems?: (onClose: () => void) => React.ReactNode;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ 
  onProfileClick, 
  onFeedbackClick,
  showName = false,
  additionalItems,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();

  // Check if user has admin portal access
  const hasAdminAccess = hasMinimumAdminAccess(user?.roles);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleProfileClick = () => {
    setIsOpen(false);
    onProfileClick();
  };

  const handleFeedbackClick = () => {
    setIsOpen(false);
    onFeedbackClick();
  };

  const handleSignOut = () => {
    setIsOpen(false);
    signOut();
  };

  const initial = user?.displayName?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button - Avatar circle + optional name + chevron */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-white/80 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500 rounded-full"
      >
        <div className="w-8 h-8 rounded-full bg-rm-accent-500/20 border border-white/20 flex items-center justify-center text-sm font-semibold text-white">
          {initial}
        </div>
        {showName && (
          <span className="hidden sm:block text-sm font-medium">
            {user?.displayName || 'User'}
          </span>
        )}
        <ChevronDown className={clsx(
          'w-4 h-4 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-xl border border-rm-neutral-200 py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-rm-neutral-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rm-primary-100 flex items-center justify-center text-sm font-bold text-rm-primary-700 flex-shrink-0">
                {initial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-rm-ink-900 truncate">
                  {user?.displayName || 'User'}
                </p>
                <p className="text-xs text-rm-neutral-500 truncate">
                  {user?.email || ''}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleProfileClick}
              className="w-full flex items-center px-4 py-2.5 text-sm text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
            >
              <Settings className="w-4 h-4 mr-3 text-rm-neutral-500" />
              Profile Settings
            </button>

            <button
              onClick={handleFeedbackClick}
              className="w-full flex items-center px-4 py-2.5 text-sm text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
            >
              <MessageCircle className="w-4 h-4 mr-3 text-rm-neutral-500" />
              App Feedback
            </button>

            {/* HR Admin Portal Link - Mobile Only (only in non-admin context) */}
            {hasAdminAccess && !additionalItems && (
              <>
                <div className="md:hidden border-t border-rm-neutral-200 my-1"></div>
                <Link
                  to="/admin"
                  onClick={() => setIsOpen(false)}
                  className="md:hidden flex items-center px-4 py-2.5 text-sm text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
                >
                  <BarChart3 className="w-4 h-4 mr-3 text-rm-neutral-500" />
                  HR
                </Link>
              </>
            )}
          </div>

          {/* Additional items (e.g. admin nav links) */}
          {additionalItems && (
            <>
              <div className="border-t border-rm-neutral-200 my-1"></div>
              {additionalItems(() => setIsOpen(false))}
            </>
          )}

          {/* Sign Out */}
          <div className="border-t border-rm-neutral-200 my-1"></div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};
